import { Button } from '@/components/ui/button';
import { Mail, MessageSquare, Phone, } from 'lucide-react';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { Page } from '@/components/layout/page';
import { HoneypotField, Input, Textarea } from '@/components/ui/field';
import { Alert, Card, CardBody, Container, Section } from '@/components/ui/primitives';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, presentError } from '@/lib/errors';

/**
 * Formulaire de contact — `POST /support/contact`.
 *
 * Les messages alimentent la boîte unifiée du site côté back-office : il n'y a
 * pas de serveur d'emails à monter, ni de service tiers à brancher.
 *
 * Protection anti-spam : l'API applique sa propre limitation de débit, et ce
 * formulaire ajoute un **champ pot de miel** invisible (voir `HoneypotField`).
 * S'il est rempli, c'est un robot : on affiche la confirmation habituelle sans
 * rien envoyer. Répondre « message envoyé » à un robot vaut mieux que de lui
 * signaler qu'il a été repéré.
 */
export default function ContactPage() {
    const site = useSite();
    const { user } = useSession();

    const [name, setName] = useState('');
    const [email, setEmail] = useState(user?.email ?? '');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [honeypot, setHoneypot] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fields = fieldErrors(error);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();

        // Pot de miel rempli : on simule le succès sans appeler l'API.
        if (honeypot.trim() !== '') {
            setSent(true);

            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await dashlite.contactSupport({
                ...(name.trim() ? { name: name.trim() } : {}),
                email: email.trim(),
                subject: subject.trim(),
                message: message.trim(),
            });

            setSent(true);
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Page title="Nous contacter">
     

            <Section>
                <Container className="grid items-start gap-10 lg:grid-cols-[1.2fr_1fr]">
                    <Card>
                        <CardBody>
                            {sent ? (
                                <Alert tone="positive" title="Message envoyé">
                                    Merci, votre message est bien arrivé. Nous vous répondons à l'adresse indiquée.
                                    <div className="mt-3">
                                        <Button variant="secondary" size="sm" onClick={() => setSent(false)}>
                                            Écrire un autre message
                                        </Button>
                                    </div>
                                </Alert>
                            ) : (
                                <form onSubmit={handleSubmit} className="relative space-y-5" noValidate>
                                    <HoneypotField value={honeypot} onChange={setHoneypot} />

                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <Input
                                            label="Nom"
                                            optional
                                            autoComplete="name"
                                            value={name}
                                            error={fields.name}
                                            onChange={(event) => setName(event.target.value)}
                                        />

                                        <Input
                                            data-dashlite="contact-email"
                                            label="Adresse email"
                                            type="email"
                                            required
                                            autoComplete="email"
                                            value={email}
                                            error={fields.email}
                                            onChange={(event) => setEmail(event.target.value)}
                                        />
                                    </div>

                                    <Input
                                        data-dashlite="contact-subject"
                                        label="Objet"
                                        required
                                        value={subject}
                                        error={fields.subject}
                                        onChange={(event) => setSubject(event.target.value)}
                                        placeholder="Question sur mon accès"
                                    />

                                    <Textarea
                                        data-dashlite="contact-message"
                                        label="Message"
                                        required
                                        value={message}
                                        error={fields.message}
                                        onChange={(event) => setMessage(event.target.value)}
                                        placeholder="Décrivez votre demande le plus précisément possible."
                                    />

                                    {error !== null && Object.keys(fields).length === 0 && (
                                        <Alert tone="critical" title={presentError(error).message}>
                                            {presentError(error).hint}
                                        </Alert>
                                    )}

                                    <Button data-dashlite="contact-submit" type="submit" size="lg" loading={submitting} className="bg-[#b20303] text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#950202] hover:shadow-lg">
                                        <MessageSquare className="size-4" aria-hidden />
                                        Envoyer le message
                                    </Button>
                                </form>
                            )}
                        </CardBody>
                    </Card>

                    <div className="space-y-6">
                        <h2 className="mb-4 font-serif text-3xl font-bold text-foreground">
                            Contactez notre équipe
                        </h2>
                        <p className="mb-8 leading-relaxed text-muted-foreground">
                            Vous avez une question sur un document ou une
                            demande spécifique ? Remplissez le formulaire
                            ci-contre, nous vous répondrons sous 24h ouvrées.
                        </p>

                        <div className="flex items-center gap-4 rounded-lg border border-line bg-cream p-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                                <Phone className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-semibold text-foreground">
                                    Par téléphone
                                </div>
                                <a
                                    href={`tel:454545`}
                                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                                >
                                    454554{' '}

                                </a>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 rounded-lg border border-line bg-cream p-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent">
                                <Mail className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-semibold text-foreground">
                                    Par email
                                </div>
                                <a
                                        href={`mailto:${site.contact_email}`}
                                        className="text-brand-700 inline-flex items-center gap-2 text-sm hover:underline"
                                    >

                                    {site.contact_email}{' '}

                                </a>
                            </div>
                        </div>

                    </div>
                </Container>
            </Section>
        </Page>
    );
}
